import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from 'src/app/core/services/base.httpService';
import { URLConstants } from 'src/app/shared/utility/url-constants';
import { ICbpEntryNumber, IMawbInformationData } from '../model/mawb.model';
import { BehaviorSubject } from 'rxjs';
import { IBasicSearchProps } from 'src/app/shared/models/dynamic.model';
import { MessagePopupComponent } from 'src/app/shared/components/message-popup/message-popup.component';
import {
  ModalPopupMessages,
  BulkDeleteItemNames,
  popupWidth,
  AttachmentDownloadRequestType,
  HttpSignatures,
  validationErrorMessages,
} from 'src/app/shared/utility/constants';
import { ModalService } from 'src/app/shared/services/modal.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { handleErrorMessage } from 'src/app/shared/operators/error.operator';

@Injectable({
  providedIn: 'root',
})
export class MawbService {
  public requiredMawbInfoData = new BehaviorSubject<any>({});
  public isFromPageSummary = new BehaviorSubject<string>('');
  public isMawbPrevSummary = new BehaviorSubject<any>('');
  public isAirlineCodeFieldFocussed = new BehaviorSubject<boolean>(false);
  private readonly errorHandler = handleErrorMessage();

  constructor(
    private readonly baseService: BaseService,
    private readonly modalService: ModalService,
    private readonly commonService: CommonService
  ) {}

  public setRequiredDataForInfoPage(data: any): void {
    this.requiredMawbInfoData.next(data);
  }

  public setPageFrom(data: string) {
    this.isFromPageSummary.next(data);
  }

  public isMawbSummaryPrevSearch(data: string) {
    this.isMawbPrevSummary.next(data);
  }

  public checkMawbExtExistence(mawbExt: string): Observable<any> {
    const url = URLConstants.checkMawbExt + mawbExt;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  /** PAC-24713: US CBP Entry Numbers grid for a MAWB. */
  public getCbpEntryNumbers(mawbId: number): Observable<{
    statusCode: number;
    message: string;
    data: ICbpEntryNumber[];
  }> {
    const url = `${URLConstants.getCbpEntryNumbers}${mawbId}`;
    return this.baseService
      .initiateApiRequest('GET', url, '', 'json')
      .pipe(this.errorHandler());
  }

  public getManifestDataByExt(data: any): Observable<any> {
    const url = `${URLConstants.getManifestData}/${data}`;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public saveMawbData(data: IMawbInformationData): Observable<any> {
    const url = URLConstants.saveMawbData;
    return this.baseService
      .initiateApiRequest('PUT', url, data, 'json')
      .pipe(this.errorHandler());
  }

  public updateMawbData(data: IMawbInformationData): Observable<any> {
    const url = URLConstants.updateMawbData;
    return this.baseService
      .initiateApiRequest('POST', url, data, 'json')
      .pipe(this.errorHandler());
  }

  public getMawbBasicSearchData(event: IBasicSearchProps) {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.getMawbList,
      event,
      '',
      'json'
    );
  }

  public fetchAirlineDetails(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.checkMawbExists,
      data,
      'json'
    );
  }

  public checkFlightForManifest(data: any) {
    const url = URLConstants.checkFlightExists;
    return this.baseService.initiateApiRequest('POST', url, data, 'json');
  }

  public deleteFlightAPI(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.deleteFlight, event, '', 'json')
      .pipe(this.errorHandler());
  }

  public deleteMawb(data: any) {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.deleteMawb, data, 'json')
      .pipe(this.errorHandler());
  }

  public deleteMawbManifests(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.deleteMawbManifests,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public saveMawbManifests(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.addMawbManifests,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public saveFlight(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.getSaveFlightDataById,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public saveViewPallets(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.saveViewModePallets,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public updateViewPallets(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.updateViewPallets,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public deleteViewPallets(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.deleteViewPallets,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public updateFlight(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.getUpdateFlightDataById,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public publishMawb(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.publishMawb,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public openDeletePopup(selection: any) {
    const message =
      selection.length > 1
        ? ModalPopupMessages.bulkDeleteMessage(
            selection.length,
            BulkDeleteItemNames.Mawbs
          )
        : ModalPopupMessages.singleDeleteMessage(
            AttachmentDownloadRequestType.mawb
          ) + `${selection[0].mawb}?`;
    const dialogRef = this.modalService.openPopup(
      {
        message: message,
        isMultiple: true,
        isFooterRequired: true,
        isBulk: selection.length > 1,
      },
      selection.length > 1
        ? ModalPopupMessages.BulkDeleteHeader
        : ModalPopupMessages.singleDeleteHeader(
            AttachmentDownloadRequestType.mawb
          ),
      MessagePopupComponent,
      popupWidth.deletePopup
    );
    return dialogRef;
  }

  public isAllPublishCriteriaFailed(responseData: any) {
    return responseData.every(
      (publishData: any) =>
        !this.commonService.isValidField(publishData.clearanceAgent) &&
        !publishData.isFlightLinked &&
        publishData.isShipmentBlocked
    );
  }

  public isAllPublishCriteriaPassed(responseData: any) {
    return responseData.every(
      (publishData: any) =>
        this.commonService.isValidField(publishData.clearanceAgent) &&
        publishData.isFlightLinked &&
        !publishData.isShipmentBlocked
    );
  }

  public getValidationMessageForPopup(responseData: any) {
    let validationMessage = '';
    if (
      !this.commonService.isValidField(responseData[0].clearanceAgent) &&
      responseData[0].isFlightLinked &&
      !responseData[0].isShipmentBlocked
    ) {
      validationMessage = validationErrorMessages.publishClearanceAgent;
    } else if (
      !responseData[0].isFlightLinked &&
      this.commonService.isValidField(responseData[0].clearanceAgent) &&
      !responseData[0].isShipmentBlocked
    ) {
      validationMessage = validationErrorMessages.publishNoFlights;
    } else if (
      responseData[0].isShipmentBlocked &&
      this.commonService.isValidField(responseData[0].clearanceAgent) &&
      responseData[0].isFlightLinked
    ) {
      validationMessage = validationErrorMessages.publishShipmentBlocked;
    } else {
      validationMessage = validationErrorMessages.publishCriteriaMismatchError;
    }
    return validationMessage;
  }

  public advanceMawb(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.advanceMawbInformation,
      data,
      'json'
    );
  }

  public manifestMatachedCriteria(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.fetchMatchedManifests,
      event,
      '',
      'json'
    );
  }

  public setFocusToAirlineCodeField(data: boolean) {
    this.isAirlineCodeFieldFocussed.next(data);
  }
}
